import { Component, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { INavigationElement } from 'src/app/components/navigation-bar/INavigationElement';
import { QualificationFormatter } from 'src/app/components/qualifications/view-qualifications/qualification-formatter';
import { IApplicant } from 'src/app/model/applicant/iapplicant';
import { IAttachments } from 'src/app/model/attachments/iattachments';
import { APPLICATION_TYPES } from 'src/app/model/constants/application-types';
import { IApplication } from 'src/app/model/iapplication';
import { ModelUtil } from 'src/app/model/model-util';
import { IQualification } from 'src/app/model/qualifications/iqualification';
import { APPLICATION_NAMES, PAGE_NAMES, QUALIFICATION_NAMES } from 'src/app/service/ansokan-form-builder/application-names';
import { getErrorText } from 'src/app/service/ansokan-form-builder/validator-errors';
import { AnsokanFormGroupService } from 'src/app/service/ansokan-form-group/ansokan-form-group.service';
import { AuthService } from 'src/app/service/auth/auth.service';
import { UtilityService } from 'src/app/service/utility/utility.service';
import { DocumentUtil } from 'src/app/util/document-util';
import { GET_PAGE_FLOW } from '../../constants/page-flows';
import { PaymentCalculator } from '../../payment-calculator';
import { ALL_DELIVERY_METHODS } from '../attachment/delivery-method';

@Component({
    selector: 'app-review',
    styleUrls: ['./review.component.scss'],
    templateUrl: './review.component.html',
    standalone: false
})
export class ReviewComponent implements OnInit {

  public confirmationControl: UntypedFormControl;
  public authorized: boolean;
  private calculator: PaymentCalculator;
  private pageFlow: INavigationElement[];

  constructor(
    private groupService: AnsokanFormGroupService,
    private authService: AuthService,
    private utilityService: UtilityService,
  ) { }

  public get qualificationError(): string {
    const group = this.groupService.getFormGroup();
    const type = group.get(APPLICATION_NAMES.APPLICATION_TYPE).value;
    return type === APPLICATION_TYPES.SUPPLEMENT_SWEDISH ? getErrorText(group) : null;
  }

  public ngOnInit() {
    // Set autorized.
    this.authService.isAuthorized()
      .pipe(catchError(() => of(false)))
      .subscribe((value) => this.authorized = value);
    // Set application root as page group.
    const group = this.groupService.getFormGroup();
    // Mark all controls as touched.
    group.markAllAsTouched();
    // Setup confirmation control.
    this.confirmationControl = group.get(APPLICATION_NAMES.USER_CONFIRMATION) as UntypedFormControl;
    this.confirmationControl.valueChanges.subscribe(() => group.updateValueAndValidity({ emitEvent: false }));
    // Set page flow.
    const type = this.groupService.get(APPLICATION_NAMES.APPLICATION_TYPE).value;
    this.pageFlow = GET_PAGE_FLOW(type);
    // Create payment calculator.
    this.calculator = new PaymentCalculator(this.applicationType);
    // Set payment value.
    const paymentControl = group.get(APPLICATION_NAMES.PAYMENT);
    paymentControl.setValue(this.payment);
  }

  private get iapplication(): IApplication {
    const data = this.groupService.value;
    return data ? data : ModelUtil.emptyApplication();
  }

  public get ssn(): string { return this.iapplication.applicantSsn; }
  public get iapplicant(): IApplicant { return this.iapplication.applicant; }
  private get applicationType(): string { return this.iapplication.type; }
  private get iattachments(): IAttachments { return this.iapplication.attachments; }
  public get payment(): number { return this.calculator.calulate(this.iapplication.degree); }
  public get isSpecialEducationTeacher(): boolean {
    return this.iapplication && this.iapplication.specialEducation && this.iapplication.specialEducation.specialEducationTeacher;
  }

  public get examinas(): { name: string, year: string }[] {
    const degree = this.iapplication.degree;
    if (!degree) {
      return null;
    }
    const result = [degree.firstExamina, degree.secondExamina]
      .filter((ex) => ex && ex.name);
    return result.length > 0 ? result : null;
  }

  public get attachments(): { name: string, size: string }[] {
    return this.iattachments.attachments
      .map((value) => ({ name: value.fileName, size: (value.size / 1048576).toFixed(2) + ' MB' }));
  }

  public get deliveryMethod(): string {
    return ALL_DELIVERY_METHODS
      .filter((method) => method.value === this.iattachments.deliveryMethod)
      .map((method) => method.description)
      .pop();
  }

  public get vocationalQualifications(): { qual: string, bases: string[] }[] {
    if (this.iapplication && this.iapplication.vocationalQualification) {
    return this.formatQualifications(this.iapplication.vocationalQualification.qualifications);
    } else {
      return null;
    }
  }

  public get otherQualifications(): { qual: string, bases: string[] }[] {
    if (this.iapplication && this.iapplication.otherQualification) {
    return this.formatQualifications(this.iapplication.otherQualification.qualifications);
    } else {
      return null;
    }
  }

  public getLabel(index: string): string {
    if (!this.pageFlow) {
      return index;
    }
    const navElement = this.pageFlow
      .filter((el) => el.index === index)
      .pop();
    return navElement ? `${this.pageFlow.indexOf(navElement) + 1}. ${navElement.text}` : null;
  }

  public generateDocument(): void {
    const application: IApplication = this.groupService.value;
    this.utilityService.generateDocument(application)
      .subscribe((result) => DocumentUtil.displayDocument(result.content));
  }

  private formatQualifications(qualifications: IQualification[]): { qual: string, bases: string[] }[] {
    return qualifications.map(
      (qualification: IQualification) => ({
        bases: QualificationFormatter.formatBases(qualification.bases),
        qual: QualificationFormatter.formatQualification(qualification).join(', '),
      }),
    );
  }
}
